/**
 * advisor-fit.js — fit score 0–100 for one normalised plan against the fact-find
 * (spec docs/superpowers/specs/2026-10-12-plan-advisor-design.md, "Fit score").
 *
 * Pure. Five components, each 0–100, combined with the rule set's weights after the client's
 * priority shifts them (advisor-rules.js effectiveWeights). A component without the data it needs
 * scores NEUTRAL (50) and says so — it never guesses — and a medical plan missing both its
 * deductible and its out-of-pocket maximum is marked limited_detail.
 */

import { effectiveWeights } from './advisor-rules.js';
import { sanitizeFacts, hasHighHealthNeed, householdHealth } from './advisor-facts.js';

export const NEUTRAL = 50;

export const MEDICAL_TYPES = ['aca', 'major-medical', 'stm', 'gap', 'limited-medical'];

// Suitability of each medical product type by household health. Short-term, gap and limited-benefit
// plans exclude pre-existing conditions, so significant needs push hard toward ACA (spec).
const SUITABILITY = {
  significant: { aca: 100, 'major-medical': 45, stm: 10, gap: 10, 'limited-medical': 5 },
  minor: { aca: 85, 'major-medical': 75, stm: 55, gap: 55, 'limited-medical': 40 },
  none: { aca: 80, 'major-medical': 80, stm: 80, gap: 75, 'limited-medical': 60 },
};

// Deductible / OOP points lost per dollar, by how much care the household expects to use.
const COST_SLOPE = { low: 1 / 300, moderate: 1 / 150, high: 1 / 80 };

function clamp(n) {
  return Math.max(0, Math.min(100, n));
}

function money(n) {
  return '$' + Math.round(n).toLocaleString('en-US');
}

function expectedNeed(facts) {
  if (hasHighHealthNeed(facts)) return 'high';
  if (facts.usage) return facts.usage;
  return householdHealth(facts) === 'minor' ? 'moderate' : null;
}

function scorePrice(plan, facts) {
  const p = plan.premium;
  const b = facts.budgetMonthly;
  if (p == null) return { score: NEUTRAL, neutral: true, reason: null };
  if (b == null) return { score: NEUTRAL, neutral: true, reason: null };
  if (p <= b) {
    const score = clamp(70 + 30 * (1 - p / b));
    return { score, reason: `${money(p)}/mo is within the ${money(b)} budget` };
  }
  const over = p / b - 1;
  return { score: clamp(70 - over * 140), reason: `${money(p)}/mo is ${money(p - b)} over the budget` };
}

function scoreCoverage(plan, facts) {
  const need = expectedNeed(facts);
  const known = [plan.deductible, plan.oopMax].filter((v) => v != null);
  if (known.length === 0) return { score: NEUTRAL, neutral: true, limited: true, reason: null };
  if (!need) return { score: NEUTRAL, neutral: true, reason: null };
  const slope = COST_SLOPE[need];
  // Out-of-pocket max matters more than the deductible once a household expects real claims.
  const dedScore = plan.deductible != null ? clamp(100 - plan.deductible * slope) : null;
  const oopScore = plan.oopMax != null ? clamp(100 - plan.oopMax * slope * 0.6) : null;
  const score = dedScore != null && oopScore != null ? dedScore * 0.4 + oopScore * 0.6
    : (dedScore != null ? dedScore : oopScore);
  let reason = null;
  if (plan.deductible != null) {
    reason = score >= 60
      ? `${money(plan.deductible)} deductible suits ${need} expected use`
      : `${money(plan.deductible)} deductible is high for ${need} expected use`;
  } else if (plan.oopMax != null) {
    reason = `${money(plan.oopMax)} out-of-pocket maximum`;
  }
  return { score, reason };
}

function scoreSuitability(plan, facts) {
  const health = hasHighHealthNeed(facts) ? 'significant' : householdHealth(facts);
  const table = SUITABILITY[health];
  const score = table[plan.productType];
  if (score == null) return { score: NEUTRAL, neutral: true, reason: null };
  let reason = null;
  if (health === 'significant' && plan.productType === 'aca') {
    reason = 'ACA covers pre-existing conditions, pregnancy and planned care';
  } else if (health === 'significant') {
    reason = 'Likely excludes the pre-existing conditions or planned care listed';
  } else if (health === 'minor' && ['stm', 'gap', 'limited-medical'].includes(plan.productType)) {
    reason = 'May exclude existing conditions';
  }
  return { score, reason };
}

/**
 * @param {Object} coverage - { doctors: { [npi]: bool }, drugs: { [rxcui]: bool } } for this plan,
 *   from the CMS coverage checks; absent when not checked (private plans, or not run)
 */
function scoreDoctorsDrugs(plan, facts, coverage) {
  const listed = facts.doctors.length + facts.drugs.length;
  if (listed === 0) return { score: NEUTRAL, neutral: true, reason: null };
  if (plan.productType !== 'aca') {
    return { score: NEUTRAL, neutral: true, warning: 'network_unverified', reason: null };
  }
  if (!coverage) return { score: NEUTRAL, neutral: true, reason: null };
  let checked = 0;
  let covered = 0;
  facts.doctors.forEach((d) => {
    const v = coverage.doctors && coverage.doctors[d.npi];
    if (v != null) { checked += 1; if (v) covered += 1; }
  });
  facts.drugs.forEach((d) => {
    const v = coverage.drugs && coverage.drugs[d.rxcui];
    if (v != null) { checked += 1; if (v) covered += 1; }
  });
  if (checked === 0) return { score: NEUTRAL, neutral: true, reason: null };
  const score = (covered / checked) * 100;
  return {
    score,
    reason: covered === checked
      ? 'All listed doctors and drugs are covered'
      : `${covered} of ${checked} listed doctors and drugs covered`,
  };
}

function scoreWants(plan, facts) {
  const parts = [];
  const reasons = [];
  if (facts.wants.hsa === true) {
    if (plan.hsaEligible == null) parts.push(NEUTRAL);
    else {
      parts.push(plan.hsaEligible ? 100 : 0);
      if (plan.hsaEligible) reasons.push('HSA-eligible');
    }
  }
  const nt = facts.wants.networkType;
  if (nt && nt !== 'any') {
    if (!plan.networkType) parts.push(NEUTRAL);
    else {
      const ok = plan.networkType === nt;
      parts.push(ok ? 100 : 30);
      if (ok) reasons.push(`${nt.toUpperCase()} network as wanted`);
    }
  }
  if (parts.length === 0) return { score: NEUTRAL, neutral: true, reason: null };
  return { score: parts.reduce((s, v) => s + v, 0) / parts.length, reason: reasons.join(', ') || null };
}

/**
 * Score one medical plan.
 * @returns {{ score:number, components:Object, reasons:string[], warnings:string[], limitedDetail:boolean }}
 */
export function scoreFit(plan, factsIn, weights, coverage) {
  const facts = sanitizeFacts(factsIn);
  const components = {
    price: scorePrice(plan, facts),
    coverage: scoreCoverage(plan, facts),
    suitability: scoreSuitability(plan, facts),
    doctorsDrugs: scoreDoctorsDrugs(plan, facts, coverage),
    wants: scoreWants(plan, facts),
  };
  const w = effectiveWeights(weights, facts.priority);
  let score = 0;
  Object.keys(components).forEach((k) => { score += components[k].score * (w[k] || 0); });
  const warnings = [];
  if (components.doctorsDrugs.warning) warnings.push(components.doctorsDrugs.warning);
  const limitedDetail = !!components.coverage.limited || plan.detailLevel === 'limited';
  if (limitedDetail) warnings.push('limited_detail');
  // Reasons in order of how much each component moved this plan's score.
  const reasons = Object.keys(components)
    .filter((k) => components[k].reason)
    .sort((a, b) => Math.abs(components[b].score - NEUTRAL) * (w[b] || 0)
      - Math.abs(components[a].score - NEUTRAL) * (w[a] || 0))
    .map((k) => components[k].reason);
  return { score: Math.round(clamp(score) * 10) / 10, components, reasons, warnings, limitedDetail };
}

/**
 * Score an add-on (dental, vision, accident, critical illness) within its own category: the
 * cheapest scores 100 and the rest by how close they come to it.
 */
export function scoreAddOn(plan, cheapestPremium) {
  if (plan.premium == null || !cheapestPremium) return { score: NEUTRAL, reasons: [] };
  return {
    score: Math.round(clamp((cheapestPremium / plan.premium) * 100) * 10) / 10,
    reasons: [`${money(plan.premium)}/mo`],
  };
}

export const AdvisorFit = { NEUTRAL, MEDICAL_TYPES, scoreFit, scoreAddOn };
